import "./TestCptCodesEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import {ITableColumn} from "../../../../../shared/models/table.model";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import {DeleteForever} from "@mui/icons-material";
import _ from "lodash";
import {CommonService} from "../../../../../shared/services";
import FormikRadioButtonGroupComponent
    from "../../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";
import {Grid} from "@material-ui/core";
import SearchComponent from "../../../../../shared/components/search/SearchComponent";
import TableV2Component from "../../../../../shared/components/table-components/table-v2/TableV2Component";
import DrawerComponent from "../../../../../shared/components/drawer/drawerComponent";
import HeadingComponent from "../../../../../shared/components/heading/headingComponent";
import CheckboxComponent from "../../../../../shared/components/core/checkbox/CheckboxComponent";
import TableWrapperV2Component
    from "../../../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import {APIConfig} from "../../../../../constants";

interface TestCptCodesEditComponentProps {
    testDetails: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const testCptValidationSchema = Yup.object().shape({
    isCptCodeRequired: Yup.boolean().required(),
    searchString: Yup.string(),
    cptCodes: Yup.array().when('isCptCodeRequired', {
        is: true,
        then: Yup.array()
            .of(Yup.number().required())
            .min(1, "Select at least one CPT")
            .required("Select CPT"),
        otherwise: Yup.array()
    })
});

interface testCptCodeSchema {
    isCptCodeRequired: boolean,
    searchString: string,
    cptCodes: number[]
}

const testCptInitialValues: testCptCodeSchema = {
    isCptCodeRequired: false,
    searchString: "",
    cptCodes: []
}

const TestCptCodesEditComponent = (props: TestCptCodesEditComponentProps) => {
    const {testDetails, onUpdateComplete, onEditCancel} = props;
    const [initialFormValue, setInitialFormValue] = useState<FormikValues>(testCptInitialValues)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedCptList, setSelectedCptList] = useState<any[]>([]);
    const [drawerTableFilter, setDrawerTableFilter] = useState({
        search: ""
    })

    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const columns: ITableColumn[] = [
        {
            id: 'select',
            label: <></>,
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            fixed: "left",
            width: '50',
            content: (item: any) => (
                <CheckboxComponent
                    name={"sample_select"}
                    size={"small"}
                    // disabled={sendOutApplicableSamples?.findIndex((sample: ISample) => sample.id === item.id) === -1}
                    disableRipple={true}
                    checked={selectedCptList?.findIndex((sample: any) => sample?.id === item?.id) > -1}
                    onUpdate={(isChecked: boolean) => {
                        handleSelectDeselectSample(isChecked, item);
                    }}/>
            )
        },
        {
            id: "cptCode",
            label: "CPT Code",
            width: 100,
            isDefaultColumn: true, align: "center"
        },
        {
            id: "description",
            label: "Description",
            width: 300,
            isDefaultColumn: true, align: "left"
        },
    ]

    const selectedTableColumns: ITableColumn[] = [
        {
            id: "cptCode",
            label: "CPT Code",
            width: 100,
            isDefaultColumn: true, align: "center"
        },
        {
            id: "description",
            label: "Description",
            width: 300,
            isDefaultColumn: true, align: "left"
        },
        {
            id: 'action',
            label: "Action",
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            width: '50',
            content: (item: any) => (
                <ButtonComponent
                    color={"error"}
                    variant={"icon"}
                    handleClick={() => {
                        const selectedCptlistAfterDelete = selectedCptList.filter((cpt: any) => cpt.id !== item.id);
                        const cptCodesAfterDelete = selectedCptlistAfterDelete.map((cpt: any) => cpt.id)
                        formikRef.current?.setFieldValue("cptCodes", cptCodesAfterDelete)
                        setSelectedCptList(selectedCptlistAfterDelete)
                    }}
                >
                    <DeleteForever/>
                </ButtonComponent>
            )
        }
    ]

    const handleSelectDeselectSample = useCallback((isChecked: boolean, cptCode: any) => {
        const updatedSampleList = _.cloneDeep(selectedCptList);
        if (isChecked) {
            updatedSampleList?.push(cptCode);
        } else {
            const cptID = cptCode?.id;
            updatedSampleList?.splice(updatedSampleList?.findIndex(item => item?.id === cptID), 1);
        }
        setSelectedCptList(updatedSampleList);
        const cptCodes = updatedSampleList.map((item) => item?.id)
        formikRef?.current && formikRef?.current?.setFieldValue("cptCodes", cptCodes)
    }, [selectedCptList]);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        const payload = _.cloneDeep(values);
        if (!payload.isCptCodeRequired) {
            payload.cptCodesDetails = []
        }
        delete payload.searchString;
        CommonService._test.addTestCptCode(testDetails?.id, payload)
            .then((response: any) => {
                setSubmitting(false);
                const updatedTestDetails = {
                    ...testDetails,
                    cptCodesDetails: response?.data?.cptCodesDetails,
                    isCptCodeRequired: response?.data?.isCptCodeRequired
                }
                onUpdateComplete(updatedTestDetails);
            }).catch((error: any) => {
            setSubmitting(false);
            CommonService._alert.showToast(error?.response?.data?.message, 'error');
        })
    }, [testDetails, onUpdateComplete]);

    useEffect(() => {
        setInitialFormValue((prev) => {
            return {
                ...prev,
                isCptCodeRequired: testDetails?.isCptCodeRequired || false,
                cptCodes: testDetails?.cptCodes || []
            }
        })
        setSelectedCptList(testDetails?.cptCodesDetails || [])
    }, [testDetails]);
    return (
        <div className={'test-cpt-codes-edit-component'}>
            <div className="t-form attachments-with-title-wrapper">
                <Formik
                    validationSchema={testCptValidationSchema}
                    initialValues={initialFormValue}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    innerRef={formikRef}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, values, isValid, setFieldValue, touched, errors, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);

                        return (
                            <Form className={"test-panel-configuration-add-holder"}>
                                {/*{JSON.stringify(values)}*/}
                                <div className="t-form-controls-wrapper">
                                    <div className="d-flex align-items-center mrg-bottom-25">
                                        <div className="radio-button-title">Is CPT code required?</div>
                                        <Field
                                            name={'isCptCodeRequired'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikRadioButtonGroupComponent
                                                        direction={"row"}
                                                        isValueBoolean={true}
                                                        titleKey={'title'}
                                                        valueKey={'code'}
                                                        options={CommonService._staticData.yesNoOptions}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    {/*<div className={'horizontal-line'}></div>*/}
                                    {
                                        values.isCptCodeRequired && (
                                            <>
                                                {/*<FormSubTitleWithTooltipComponent title={"All Linked CPT Codes"}/>*/}
                                                <Grid container spacing={2} alignItems={"center"}>
                                                    <Grid item md={6} lg={6}>
                                                        <Field name={"searchString"}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <SearchComponent label={""}
                                                                                     disabled={values.cptCodes.length < 1}
                                                                                     placeholder={"Search CPT Codes"}
                                                                                     onChange={(e) => {
                                                                                         setFieldValue("searchString", e)
                                                                                     }}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                    <Grid item md={3} lg={3}>
                                                        <ButtonComponent
                                                            color={"secondary"}
                                                            variant={"outlined"}
                                                            handleClick={() => setIsDrawerOpen(true)}
                                                        >
                                                            Link CPT Code
                                                        </ButtonComponent>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )
                                    }
                                    {
                                        values?.isCptCodeRequired && values.cptCodes.length > 0 && (
                                            <div className={'t-table-container mrg-top-25'}>
                                                <TableV2Component
                                                    columns={selectedTableColumns}
                                                    data={values.searchString ? selectedCptList.filter((item) => item.cptCode.toLowerCase().includes(values.searchString.toLowerCase())) : selectedCptList}
                                                />
                                            </div>
                                        )
                                    }
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={onEditCancel}
                                    >
                                        Close
                                    </ButtonComponent>

                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        // handleClick={() => {
                                        //     next(values?.list);
                                        // }}
                                        isLoading={isSubmitting}
                                    >
                                        Save Changes
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
            <DrawerComponent
                className={"drawer-wrapper test-icd-code-drawer"}
                // title={'Lab Details'}
                open={isDrawerOpen}
                onClose={() => {
                    setIsDrawerOpen(false);
                }}
            >
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Select CPT Code(s)"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef?.current && formikRef?.current.setFieldValue("cptCodes", [])
                             setSelectedCptList([])
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <SearchComponent label={""} placeholder={"Search"} value={drawerTableFilter?.search}
                                 onChange={(e) => setDrawerTableFilter((prev) => {
                                     return {
                                         ...prev,
                                         search: e
                                     }
                                 })}/>
                <div className="table-list-container">
                    <TableWrapperV2Component
                        isPaginated={true}
                        columns={columns}
                        url={APIConfig.GET_CPT_CODE_LIST_TABLE.URL}
                        method={APIConfig.GET_CPT_CODE_LIST_TABLE.METHOD}
                        extraPayload={drawerTableFilter}
                        isColumnFilterVisible={true}
                        // onDataLoad={(data: any) => setData(data)}
                    />
                </div>
            </DrawerComponent>
        </div>
    );

};

export default TestCptCodesEditComponent;
